/**
 * Form Interceptor
 *
 * Intercepts form submissions to enable offline support for petitions and
 * consultations. Integrates with existing form handlers without breaking changes.
 *
 * @package DemocraticServices
 * @since 3.1.0
 * @author Democratic Services Team
 *
 * Design Decisions:
 * - Progressive enhancement - forms work normally when online
 * - Non-intrusive - doesn't modify existing form markup
 * - Graceful degradation - falls back to normal submit if PWA unavailable
 * - Uses existing WordPress AJAX handlers
 */

import { queueAction, ACTION_TYPES } from './offline-queue.js';
import { getOnlineStatus, registerBackgroundSync } from './background-sync.js';
import { notifyActionQueued } from './offline-feedback.js';
import { clearFormDraft } from './draft-manager.js';

// =============================================================================
// CONFIGURATION
// =============================================================================

const INTERCEPTOR_CONFIG = {
	// Form selectors to intercept
	petitionSignForms: [
		'#sign-petition-form',
		'#petition-signature-form',
		'.signature-form',
		'form[action*="sign_petition"]',
		'form[data-action="sign_petition"]',
		'.petition-sign-form',
	],

	petitionCreateForms: [
		'#create-petition-form',
		'#petition-create-form',
		'form[action*="create_petition"]',
		'form[data-action="create_petition"]',
		'.petition-create-form',
	],

	consultationForms: [
		'#consultation-response-form',
		'#consultation-form',
		'#submit-consultation-response',
		'form[action*="consultation"]',
		'form[data-action="submit_consultation"]',
		'.consultation-response-form',
	],

	// Newsletter forms - handled by newsletter-modal.js, not PWA interceptor
	// These forms use data-no-pwa-intercept="true" attribute
	newsletterForms: [],

	// Vote forms
	voteForms: ['#cast-vote-form', '.vote-form', 'form[data-action="cast_vote"]'],

	// Call-in forms (admin only but still useful offline)
	callInForms: [
		'.call-in-form',
		'form[id^="call-in-form-"]',
		'form[data-action="call_in_decision"]',
	],

	// WordPress AJAX actions
	ajaxActions: {
		signPetition: 'sign_petition',
		createPetition: 'create_petition',
		consultationResponse: 'submit_consultation_response',
		newsletterSignup: 'bcp_newsletter_signup',
		castVote: 'democratic_cast_vote',
		callInDecision: 'democratic_call_in_decision',
	},

	// Request timeout (ms)
	requestTimeout: 30000,
};

// =============================================================================
// STATE
// =============================================================================

/**
 * Set of intercepted forms
 * @type {Set<HTMLFormElement>}
 */
const interceptedForms = new Set();

/**
 * Track if form interceptor is already initialized
 * @type {boolean}
 */
let isInitialized = false;

// =============================================================================
// FORM INTERCEPTION
// =============================================================================

/**
 * Intercept a form for offline support
 *
 * @param {HTMLFormElement} form - The form to intercept
 * @param {string} actionType - The action type from ACTION_TYPES
 * @param {string} ajaxAction - The WordPress AJAX action name
 */
function interceptForm(form, actionType, ajaxAction) {
	if (interceptedForms.has(form)) {
		return;
	}

	// Skip forms that are already handled by inline scripts
	if (form.getAttribute('data-no-pwa-intercept') === 'true') {
		return;
	}

	interceptedForms.add(form);
	form.setAttribute('data-offline-enabled', 'true');

	// Add our interceptor
	form.addEventListener('submit', async (event) => {
		// Don't intercept if marked to skip
		if (form.getAttribute('data-skip-intercept') === 'true') {
			form.removeAttribute('data-skip-intercept');
			return;
		}

		// Prevent default submission
		event.preventDefault();
		event.stopPropagation();

		// Get form data
		const formData = new FormData(form);

		// Check if online
		if (getOnlineStatus()) {
			// Try normal submission
			const success = await submitFormOnline(
				form,
				formData,
				ajaxAction,
				actionType,
			);

			if (success) {
				// Clear draft after successful submission
				clearFormDraft(form);
				return;
			}
		}

		// Offline or submission failed - queue the action
		await queueFormOffline(form, formData, actionType, ajaxAction);
	});

	// Form intercepted (silent)
}

/**
 * Submit form online via AJAX
 *
 * @param {HTMLFormElement} form - The form element
 * @param {FormData} formData - The form data
 * @param {string} ajaxAction - WordPress AJAX action
 * @param {string} actionType - Action type for logging
 * @returns {Promise<boolean>} True if successful
 */
async function submitFormOnline(form, formData, ajaxAction, actionType) {
	// Show loading state
	const submitButton = form.querySelector('[type="submit"]');
	const originalButtonText = submitButton?.textContent;

	if (submitButton) {
		submitButton.disabled = true;
		submitButton.textContent = 'Submitting...';
	}

	try {
		// Add AJAX action (replace any existing action field from the form).
		formData.set('action', ajaxAction);

		// Get AJAX URL
		const ajaxUrl = getAjaxUrl();

		// Create abort controller for timeout
		const controller = new AbortController();
		const timeoutId = setTimeout(
			() => controller.abort(),
			INTERCEPTOR_CONFIG.requestTimeout,
		);

		// Make request
		const response = await fetch(ajaxUrl, {
			method: 'POST',
			body: formData,
			credentials: 'same-origin',
			signal: controller.signal,
		});

		clearTimeout(timeoutId);

		if (!response.ok) {
			throw new Error(`HTTP ${response.status}`);
		}

		const data = await response.json();

		if (data.success === false) {
			throw new Error(
				data.data?.message || data.message || 'Submission failed',
			);
		}

		// Success!
		handleSubmitSuccess(form, data, actionType);
		return true;
	} catch (error) {
		// Online submission failed (silent)

		// Check if it's a network error (should queue) or server error (show error)
		if (isNetworkError(error)) {
			return false; // Will be queued
		}

		// Show error to user
		handleSubmitError(form, error.message);
		return true; // Don't queue - it's a real error
	} finally {
		// Restore button state
		if (submitButton) {
			submitButton.disabled = false;
			submitButton.textContent = originalButtonText;
		}
	}
}

/**
 * Generate a unique submission ID to prevent duplicate submissions
 *
 * Uses crypto.randomUUID when available, falls back to timestamp + random string.
 * This ID is sent with the submission and checked server-side.
 *
 * @returns {string} Unique submission ID
 */
function generateSubmissionId() {
	const timestamp = Date.now().toString(36);
	const random =
		typeof crypto !== 'undefined' && crypto.randomUUID
			? crypto.randomUUID().split('-')[0]
			: Math.random().toString(36).substring(2, 10);
	return `${timestamp}-${random}`;
}

/**
 * Queue form submission for offline sync
 *
 * @param {HTMLFormElement} form - The form element
 * @param {FormData} formData - The form data
 * @param {string} actionType - Action type from ACTION_TYPES
 * @param {string} ajaxAction - WordPress AJAX action
 * @returns {Promise<number>} The ID of the queued action
 */
async function queueFormOffline(form, formData, actionType, ajaxAction) {
	// Convert FormData to object
	const data = {};
	for (const [key, value] of formData.entries()) {
		data[key] = value;
	}

	// Add metadata
	data._ajax_action = ajaxAction;

	// Generate unique submission ID to prevent duplicates on sync
	const submissionId = generateSubmissionId();
	data._submission_id = submissionId;

	// Queue the action with submission ID for deduplication
	const actionId = await queueAction({
		type: actionType,
		url: getAjaxUrl(),
		method: 'POST',
		data: data,
		submissionId: submissionId,
		metadata: {
			formId: form.id,
			pageUrl: window.location.href,
			timestamp: Date.now(),
			submissionId: submissionId,
		},
	});

	// Register for background sync
	const syncTag = getSyncTag(actionType);
	await registerBackgroundSync(syncTag);

	// Notify user
	const actionName = getActionDisplayName(actionType);
	notifyActionQueued(actionName);

	// Show success message on form
	showQueuedMessage(form, actionType);

	// Clear draft since we've saved the data
	clearFormDraft(form);

	return actionId;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get the WordPress AJAX URL
 *
 * @returns {string} AJAX URL
 */
function getAjaxUrl() {
	// Try to get from WordPress localized script
	if (
		typeof window.democraticServices !== 'undefined' &&
    window.democraticServices.ajaxUrl
	) {
		return window.democraticServices.ajaxUrl;
	}

	if (typeof window.ajaxurl !== 'undefined') {
		return window.ajaxurl;
	}

	// Fallback to default WordPress AJAX path
	return '/wp-admin/admin-ajax.php';
}

/**
 * Get the sync tag for an action type
 *
 * @param {string} actionType - Action type
 * @returns {string} Sync tag
 */
function getSyncTag(actionType) {
	switch (actionType) {
		case ACTION_TYPES.PETITION_SIGNATURE:
		case ACTION_TYPES.PETITION_CREATE:
			return 'sync-petition-signature';
		case ACTION_TYPES.CONSULTATION_RESPONSE:
			return 'sync-consultation-response';
		default:
			return 'sync-action';
	}
}

/**
 * Get display name for an action type
 *
 * @param {string} actionType - Action type
 * @returns {string} Display name
 */
function getActionDisplayName(actionType) {
	switch (actionType) {
		case ACTION_TYPES.PETITION_SIGNATURE:
			return 'petition signature';
		case ACTION_TYPES.PETITION_CREATE:
			return 'petition';
		case ACTION_TYPES.CONSULTATION_RESPONSE:
			return 'consultation response';
		default:
			return 'action';
	}
}

/**
 * Check if an error is a network error
 *
 * @param {Error} error - The error
 * @returns {boolean} True if network error
 */
function isNetworkError(error) {
	// Abort errors (timeout) are network-related
	if (error.name === 'AbortError') {
		return true;
	}

	// TypeError usually indicates network failure
	if (error instanceof TypeError) {
		return true;
	}

	// Check message for network-related errors
	const networkMessages = [
		'failed to fetch',
		'network error',
		'net::',
		'networkerror',
		'connection refused',
		'timeout',
	];

	const message = error.message?.toLowerCase() || '';
	return networkMessages.some((msg) => message.includes(msg));
}

/**
 * Handle successful form submission
 *
 * @param {HTMLFormElement} form - The form
 * @param {Object} data - Response data
 * @param {string} actionType - Action type
 */
function handleSubmitSuccess(form, data, actionType) {
	// Check for redirect URL in response
	if (data.redirect_url || data.redirectUrl || data.data?.redirect) {
		window.location.href =
      data.redirect_url || data.redirectUrl || data.data.redirect;
		return;
	}

	// Show success message
	const successMessage =
    data.message || data.data?.message || getSuccessMessage(actionType);
	showSuccessMessage(form, successMessage);

	// Reset form
	form.reset();

	// Dispatch success event
	form.dispatchEvent(
		new CustomEvent('submitSuccess', {
			bubbles: true,
			detail: { data, actionType },
		}),
	);
}

/**
 * Handle form submission error
 *
 * @param {HTMLFormElement} form - The form
 * @param {string} errorMessage - Error message
 */
function handleSubmitError(form, errorMessage) {
	// Find or create error container
	let errorContainer = form.querySelector('.form-error-message');

	if (!errorContainer) {
		errorContainer = document.createElement('div');
		errorContainer.className = 'form-error-message';
		errorContainer.setAttribute('role', 'alert');
		form.insertBefore(errorContainer, form.firstChild);
	}

	errorContainer.innerHTML = `
    <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
      <p class="font-medium">Submission failed</p>
      <p class="text-sm">${escapeHtml(errorMessage)}</p>
    </div>
  `;

	// Scroll to error
	errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Show success message on form
 *
 * @param {HTMLFormElement} form - The form
 * @param {string} message - Success message
 */
function showSuccessMessage(form, message) {
	// Find or create success container
	let successContainer = form.querySelector('.form-success-message');

	if (!successContainer) {
		successContainer = document.createElement('div');
		successContainer.className = 'form-success-message';
		successContainer.setAttribute('role', 'status');
		form.insertBefore(successContainer, form.firstChild);
	}

	successContainer.innerHTML = `
    <div class="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
      <p class="font-medium">✓ ${escapeHtml(message)}</p>
    </div>
  `;

	// Scroll to message
	successContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * Show queued message on form (for offline submissions)
 *
 * @param {HTMLFormElement} form - The form
 * @param {string} actionType - Action type
 */
function showQueuedMessage(form, actionType) {
	const actionName = getActionDisplayName(actionType);

	// Find or create message container
	let messageContainer = form.querySelector('.form-queued-message');

	if (!messageContainer) {
		messageContainer = document.createElement('div');
		messageContainer.className = 'form-queued-message';
		messageContainer.setAttribute('role', 'status');
		form.insertBefore(messageContainer, form.firstChild);
	}

	messageContainer.innerHTML = `
    <div class="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg mb-4">
      <p class="font-medium">💾 Saved for later</p>
      <p class="text-sm">Your ${escapeHtml(actionName)} will be submitted automatically when you're back online.</p>
    </div>
  `;

	// Scroll to message
	messageContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });

	// Reset form after showing message
	form.reset();
}

/**
 * Get default success message for action type
 *
 * @param {string} actionType - Action type
 * @returns {string} Success message
 */
function getSuccessMessage(actionType) {
	switch (actionType) {
		case ACTION_TYPES.PETITION_SIGNATURE:
			return 'Thank you for signing the petition!';
		case ACTION_TYPES.PETITION_CREATE:
			return 'Your petition has been submitted for review.';
		case ACTION_TYPES.CONSULTATION_RESPONSE:
			return 'Thank you for your response.';
		default:
			return 'Submitted successfully.';
	}
}

/**
 * Escape HTML to prevent XSS
 *
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
	const div = document.createElement('div');
	div.textContent = str;
	return div.innerHTML;
}

// =============================================================================
// INITIALIZATION
// =============================================================================

/**
 * Initialize form interception for all matching forms
 */
export function initFormInterceptor() {
	// Prevent duplicate initialization
	if (isInitialized) {
		return;
	}
	isInitialized = true;

	// Safe querySelectorAll: empty selector throws in some browsers
	function queryFormElements(selectorList) {
		const selector = Array.isArray(selectorList) ? selectorList.join(', ') : '';
		if (!selector || !selector.trim()) return [];
		return Array.from(document.querySelectorAll(selector));
	}

	// Intercept petition sign forms
	queryFormElements(INTERCEPTOR_CONFIG.petitionSignForms).forEach((form) => {
		if (form instanceof HTMLFormElement) {
			interceptForm(
				form,
				ACTION_TYPES.PETITION_SIGNATURE,
				INTERCEPTOR_CONFIG.ajaxActions.signPetition,
			);
		}
	});

	// Intercept petition create forms
	queryFormElements(INTERCEPTOR_CONFIG.petitionCreateForms).forEach((form) => {
		if (form instanceof HTMLFormElement) {
			interceptForm(
				form,
				ACTION_TYPES.PETITION_CREATE,
				INTERCEPTOR_CONFIG.ajaxActions.createPetition,
			);
		}
	});

	// Intercept consultation forms
	queryFormElements(INTERCEPTOR_CONFIG.consultationForms).forEach((form) => {
		if (form instanceof HTMLFormElement) {
			interceptForm(
				form,
				ACTION_TYPES.CONSULTATION_RESPONSE,
				INTERCEPTOR_CONFIG.ajaxActions.consultationResponse,
			);
		}
	});

	// Intercept newsletter forms (selector may be empty array)
	queryFormElements(INTERCEPTOR_CONFIG.newsletterForms).forEach((form) => {
		if (form instanceof HTMLFormElement) {
			interceptForm(
				form,
				ACTION_TYPES.NEWSLETTER_SIGNUP,
				INTERCEPTOR_CONFIG.ajaxActions.newsletterSignup,
			);
		}
	});

	// Intercept vote forms
	queryFormElements(INTERCEPTOR_CONFIG.voteForms).forEach((form) => {
		if (form instanceof HTMLFormElement) {
			interceptForm(
				form,
				ACTION_TYPES.VOTE_CAST,
				INTERCEPTOR_CONFIG.ajaxActions.castVote,
			);
		}
	});

	// Intercept call-in forms
	queryFormElements(INTERCEPTOR_CONFIG.callInForms).forEach((form) => {
		if (form instanceof HTMLFormElement) {
			interceptForm(
				form,
				ACTION_TYPES.CALL_IN_DECISION,
				INTERCEPTOR_CONFIG.ajaxActions.callInDecision,
			);
		}
	});

	// Watch for dynamically added forms
	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node instanceof HTMLFormElement) {
					checkAndInterceptForm(node);
				} else if (node instanceof Element) {
					node.querySelectorAll('form').forEach((form) => {
						if (form instanceof HTMLFormElement) {
							checkAndInterceptForm(form);
						}
					});
				}
			}
		}
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});

	// Form interceptor initialized (silent)
}

/**
 * Check if a form should be intercepted and do so
 *
 * @param {HTMLFormElement} form - The form to check
 */
function checkAndInterceptForm(form) {
	function matchesAny(formEl, selectorList) {
		const selector = Array.isArray(selectorList) ? selectorList.join(', ') : '';
		return selector && selector.trim() && formEl.matches(selector);
	}

	if (matchesAny(form, INTERCEPTOR_CONFIG.petitionSignForms)) {
		interceptForm(
			form,
			ACTION_TYPES.PETITION_SIGNATURE,
			INTERCEPTOR_CONFIG.ajaxActions.signPetition,
		);
	} else if (matchesAny(form, INTERCEPTOR_CONFIG.petitionCreateForms)) {
		interceptForm(
			form,
			ACTION_TYPES.PETITION_CREATE,
			INTERCEPTOR_CONFIG.ajaxActions.createPetition,
		);
	} else if (matchesAny(form, INTERCEPTOR_CONFIG.consultationForms)) {
		interceptForm(
			form,
			ACTION_TYPES.CONSULTATION_RESPONSE,
			INTERCEPTOR_CONFIG.ajaxActions.consultationResponse,
		);
	} else if (matchesAny(form, INTERCEPTOR_CONFIG.newsletterForms)) {
		interceptForm(
			form,
			ACTION_TYPES.NEWSLETTER_SIGNUP,
			INTERCEPTOR_CONFIG.ajaxActions.newsletterSignup,
		);
	} else if (matchesAny(form, INTERCEPTOR_CONFIG.voteForms)) {
		interceptForm(
			form,
			ACTION_TYPES.VOTE_CAST,
			INTERCEPTOR_CONFIG.ajaxActions.castVote,
		);
	} else if (matchesAny(form, INTERCEPTOR_CONFIG.callInForms)) {
		interceptForm(
			form,
			ACTION_TYPES.CALL_IN_DECISION,
			INTERCEPTOR_CONFIG.ajaxActions.callInDecision,
		);
	}
}

/**
 * Intercept jQuery AJAX calls for offline support
 *
 * This hooks into jQuery's ajaxError to catch network failures
 * and queue actions for background sync.
 */
function setupJQueryAjaxInterceptor() {
	if (typeof jQuery === 'undefined' && typeof $ === 'undefined') {
		return;
	}

	const jq = typeof jQuery !== 'undefined' ? jQuery : $;

	// Hook into jQuery AJAX error handling
	jq(document).ajaxError(function (event, jqXHR, ajaxSettings, thrownError) {
		// Check if this is a network error (status 0 = no connection)
		if (jqXHR.status === 0 || thrownError === 'timeout' || !navigator.onLine) {
			const url = ajaxSettings.url || '';

			// Check if this is a petition or consultation action
			if (url.includes('admin-ajax.php')) {
				const data = ajaxSettings.data;
				let actionType = null;
				const formData = {};

				// Parse form data
				if (data instanceof FormData) {
					actionType = data.get('action');
					data.forEach((value, key) => {
						formData[key] = value;
					});
				} else if (typeof data === 'string') {
					const params = new URLSearchParams(data);
					actionType = params.get('action');
					params.forEach((value, key) => {
						formData[key] = value;
					});
				}

				// Queue if it's a petition or consultation action
				if (
					actionType &&
          (actionType.includes('petition') ||
            actionType.includes('consultation'))
				) {
					// Network error detected; queueing action

					// Determine action type for queue
					let queueActionType = ACTION_TYPES.PETITION_SIGNATURE;
					if (actionType.includes('create')) {
						queueActionType = ACTION_TYPES.PETITION_CREATE;
					} else if (actionType.includes('consultation')) {
						queueActionType = ACTION_TYPES.CONSULTATION_RESPONSE;
					}

					// Generate submission ID for server-side deduplication and nonce bypass
					const submissionId = generateSubmissionId();
					formData._submission_id = submissionId;

					// Queue the action
					queueAction({
						type: queueActionType,
						url: url,
						data: formData,
						submissionId: submissionId,
						timestamp: Date.now(),
					})
						.then(() => {
							notifyActionQueued(queueActionType);
							registerBackgroundSync(queueActionType);
						})
						.catch(() => {
							// Failed to queue action (silent)
						});
				}
			}
		}
	});

	// jQuery AJAX interceptor set up (silent)
}

/**
 * Clear queued message containers from forms
 *
 * Called when offline actions are successfully synced to remove
 * the "Saved for later" notification.
 */
function clearQueuedMessages() {
	const messages = document.querySelectorAll('.form-queued-message');
	messages.forEach((msg) => {
		// Fade out animation
		msg.style.transition = 'opacity 0.3s ease';
		msg.style.opacity = '0';
		setTimeout(() => msg.remove(), 300);
	});
}

/**
 * Set up listener for sync completion events
 */
function setupSyncCompleteListener() {
	if (typeof window === 'undefined') return;

	// Clear queued messages when actions are successfully synced
	window.addEventListener('backgroundSync:actionSynced', (event) => {
		const { success } = event.detail || {};
		if (success) {
			clearQueuedMessages();
		}
	});

	// Also clear on sync complete (batch)
	window.addEventListener('backgroundSync:syncCompleted', (event) => {
		const { success } = event.detail || {};
		if (success > 0) {
			clearQueuedMessages();
		}
	});
}

// Auto-initialize if in browser environment
if (typeof window !== 'undefined') {
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			initFormInterceptor();
			setupJQueryAjaxInterceptor();
			setupSyncCompleteListener();
		});
	} else {
		initFormInterceptor();
		setupJQueryAjaxInterceptor();
		setupSyncCompleteListener();
	}
}
